import {getCookie} from '@wandb/weave/common/util/cookie';
import {HTTPError} from '@wandb/weave/errors';

// Vendored Stainless client in weave-public after #7785.
// oxlint-disable-next-line wandb/no-relative-imports-of-files-outside-workspace-root
import {
  APIConnectionError,
  APIError,
  WeaveTrace,
} from '../../../../services/weave-trace/weave-public/sdks/node/src/vendor/weave-server-sdk';

const TRACE_SERVER_TIMEOUT_MS = 5 * 60 * 1000;

// DirectClient used response.statusText. Bun/happy-dom leave that empty, so
// keep the spec reason phrases as a fallback. 422 showed up as "HTTP 422" in
// Meticulous because it was missing from the first map.
const HTTP_REASON_PHRASE: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  408: 'Request Timeout',
  409: 'Conflict',
  413: 'Payload Too Large',
  415: 'Unsupported Media Type',
  422: 'Unprocessable Entity',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
};

export function createStainlessTraceClient(baseUrl: string): WeaveTrace {
  return new WeaveTrace({
    username: '',
    password: '',
    baseURL: new URL(baseUrl, window.location.origin).href,
    maxRetries: 0,
    timeout: TRACE_SERVER_TIMEOUT_MS,
    fetch: (url, init) => {
      // Do not forward Stainless platform headers (x-stainless-*, SDK
      // User-Agent). DirectClient never sent them; they force a CORS
      // preflight and make recorded-session matching pick the wrong HAR.
      // Accept is not one of those: /calls/stream_query picks its response
      // format from it, so forward it for that route and nowhere else.
      const incoming = new Headers(init?.headers);
      const headers = new Headers();
      const contentType = incoming.get('Content-Type');
      if (contentType) {
        headers.set('Content-Type', contentType);
      }
      const accept = incoming.get('Accept');
      if (
        accept &&
        new URL(String(url)).pathname.endsWith('/calls/stream_query')
      ) {
        headers.set('Accept', accept);
      }
      headers.set('Authorization', 'Basic ' + btoa(':'));
      headers.set('X-Origin', window.location.origin);
      if (getCookie('use_admin_privileges') === 'true') {
        headers.set('use-admin-privileges', 'true');
      }
      // Stainless already built an absolute URL. Same exception as DirectClient.
      // oxlint-disable-next-line wandb/no-unprefixed-urls
      return fetch(url, {...init, headers, credentials: 'include'});
    },
  });
}

export function throwAsHTTPError(err: unknown): never {
  if (err instanceof APIConnectionError) {
    if (err.cause instanceof Error) {
      throw err.cause;
    }
    throw new TypeError('Failed to fetch');
  }
  if (err instanceof APIError && typeof err.status === 'number') {
    const status = err.status;
    let body: string | undefined;
    if (typeof err.error === 'string') {
      body = err.error;
    } else if (err.error != null) {
      body = JSON.stringify(err.error);
    }
    // DirectClient used response.statusText. Stainless message includes the JSON body.
    const message =
      new Response(null, {status}).statusText ||
      HTTP_REASON_PHRASE[status] ||
      `HTTP ${status}`;
    throw new HTTPError(message, status, body);
  }
  throw err;
}
